import React, { useEffect } from "react";
import { Col, Form, Input, Row, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { loadAllIndustry } from "@/redux/rtk/features/CRM/industry/industrySlice";
import { loadAllCompanyType } from "@/redux/rtk/features/CRM/companyType/companyTypeSlice";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { loadAllContactSource } from "@/redux/rtk/features/CRM/contactSource/contactSourceSlice";
import BigDrawer from "@/components/Drawer/BigDrawer";
import AddIndustry from "../Setup/CompanySetup/Industry/AddIndustry";
import AddCompanyType from "../Setup/CompanySetup/CompanyType/AddCompanyType";
import { Checkbox } from "antd";

const CompanyInformation = ({ company, form }) => {
  const { t } = useTranslation();
  const { list: industryList, loading: industryLoading } = useSelector(
    (state) => state.industry
  );
  const { list: companyTypeList, loading: companyTypeLoading } = useSelector(
    (state) => state.companyType
  );
  const { list: ownerList, loading: ownerLoading } = useSelector(
    (state) => state.users
  );
  const { list: contactSourceList, loading: contactSourceLoading } = useSelector(
    (state) => state.contactSource
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAllIndustry());
    dispatch(loadAllCompanyType());
    dispatch(loadAllStaff({ query: "all" }));
    dispatch(loadAllContactSource());
  }, [dispatch]);

  useEffect(() => {
    if (company) {
      form.setFieldsValue({
        ...company,
      });
    }
  }, [company, form]);
  return (
    <>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={t("company.firstName", "First Name")}
            name="firstName"
            rules={[{ required: true, message: t("company.firstNameRequired", "First name is required") }]}>
            <Input placeholder={t("company.firstName", "First Name")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t("company.lastName", "Last Name")}
            name="lastName"
            rules={[{ required: true, message: t("company.lastNameRequired", "Last name is required") }]}>
            <Input placeholder={t("company.lastName", "Last Name")} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("common.createBy")} name="companyOwnerId">
            <Select
              style={{ width: "100%" }}
              loading={ownerLoading}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
              placeholder={t("company.selectOwner")}>
              {ownerList?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item?.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("company.contactPerson")} name="contactPerson">
            <Input placeholder={t("company.contactPerson")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t("company.website")} name="website">
            <Input placeholder={t("company.website")} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={t("common.email")}
            name="email"
            rules={[
              { required: true, message: t("company.emailRequired") },
              {
                type: "email",
                message: t("common.invalidEmail", "Please enter a valid email address"),
              },
            ]}>
            <Input placeholder={t("common.email")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t("common.phone")}
            name="phone"
            rules={[
              {
                pattern: /^\+?[0-9]+$/,
                message: t("common.numericPlusOnly", "Please enter only numbers and +"),
              },
            ]}>
            <Input placeholder={t("common.phone")} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12} className="flex items-center">
          <div className="pt-8">
            <span className="text-gray-500 mr-4">{t("company.passwordDefault")}</span>
            <Form.Item name="mustChangePassword" valuePropName="checked" noStyle>
              <Checkbox>{t("company.changeAfterLogin")}</Checkbox>
            </Form.Item>
          </div>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("company.type")} name="companyTypeId">
            <Select
              style={{ width: "100%" }}
              loading={companyTypeLoading}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
              placeholder={t("company.selectCompanyTypePlaceholder")}>
              {companyTypeList?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.companyTypeName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t("navigation.industry")} name="industryId">
            <Select
              style={{ width: "100%" }}
              loading={industryLoading}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
              placeholder={t("company.selectIndustryPlaceholder")}>
              {industryList?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.industryName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("company.accountSource")} name="accountSourceId">
            <Select
              style={{ width: "100%" }}
              loading={contactSourceLoading}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
              placeholder={t("company.accountSource")}>
              {contactSourceList?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.contactSourceName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("company.rating")} name="rating">
            <Select placeholder={t("company.rating")}>
              <Select.Option value="Shut Down">{t("common.shutDown")}</Select.Option>
              <Select.Option value="VIP">{t("common.vip")}</Select.Option>
              <Select.Option value="Active">{t("common.active")}</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t("company.employeesCount")} name="employeesCount">
            <Input type="number" placeholder={t("company.employeesCount")} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("company.studentsNumber")} name="studentsNumber">
            <Input type="number" placeholder={t("company.studentsNumber")} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("company.linkedin")} name="linkedin">
            <Input placeholder={t("company.linkedin")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t("company.twitter")} name="twitter">
            <Input placeholder={t("company.twitter")} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t("company.instagram")} name="instagram">
            <Input placeholder={t("company.instagram")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t("company.facebook")} name="facebook">
            <Input placeholder={t("company.facebook")} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default CompanyInformation;
